import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReportSelector from '../components/ReportSelector';
import GroupingSelector from '../components/GroupingSelector';
import DimensionSelector from '../components/DimensionSelector';
import MetricSelector from '../components/MetricSelector';
import AnalyticsTable from '../components/AnalyticsTable';
import ExportCSV from '../components/ExportCSV';
import { fetchTopicStatsReport, fetchQuestionCountReport, clearAnalyticsCache } from '../api/Reports';
import { filterNodes,excludeNodes, sortNodes, collectLeafNodes, filterTreeByLeafPredicate, sortFlatRows, collectPresentKeys, collectColumnValues } from '../utils/analyticsUtils';
import { getPresetsForReport, getPresetById } from '../utils/analyticsPresets';
import SideBar from '../components/SideBar';
import { Components } from '../assets/Dimensions';
import BackButton from '../components/Base/BackButton';

const DEFAULT_DIMENSIONS = [];
const TONE_COLORS = { success: '#28a745', warning: '#fd7e14', danger: '#dc3545', critical: '#8b0000' };
const TONE_LABELS = { success: 'Geçerli', warning: 'Uyarı', danger: 'Uygunsuz', critical: 'Kritik' };
const MUTED_COLOR = '#adb5bd';
// The page opens on the first grouping of the default (questionCount) tab.
const INITIAL_PRESET = getPresetsForReport('questionCount')[0] || null;
const user = JSON.parse(localStorage.getItem("user"));
const userAdminToken = sessionStorage.getItem("jwtP");
const generateId = () => Math.random().toString(36).substr(2, 9);

const transformData = (data, level = 0, parentDimensions = {}) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map(row => {
    const currentDimensions = { ...parentDimensions, ...(row.Dimensions || {}) };
    let children = [];
    if (row.Breakdowns && row.Breakdowns.length > 0) {
      children = transformData(row.Breakdowns, level + 1, currentDimensions);
    }
    
    return {
      id: generateId(),
      level: level,
      dimensions: currentDimensions,
      metrics: row.Metrics || {},
      children: children
    };
  });
};

const mapFiltersToBackend = (filters) => {
  const mapping = {
    "Class": "ClassId",
    "ExamType": "ExamType",
    "SegmentId": "SegmentId",
    "CourseId": "CourseId",
    "SegmentCourseId": "SegmentCourseId",
    "AchievementId": "AchievementId",
    "AchievementVideoId": "AchievementVideoId",
    "PartName": "PartName"
  };

  const mapped = {};
  Object.keys(filters).forEach(key => {
    const backendKey = mapping[key] || key;
    mapped[backendKey] = filters[key];
  });
  return mapped;
};

const Spinner = React.memo(() => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
    <svg width="40" height="40" viewBox="0 0 50 50" style={{ animation: 'rotate 2s linear infinite' }}>
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="#28a745"
        strokeWidth="5"
        strokeLinecap="round"
        style={{ strokeDasharray: '90, 150', animation: 'dash 1.5s ease-in-out infinite' }}
      />
    </svg>
    <style>{`
      @keyframes rotate {
        100% { transform: rotate(360deg); }
      }
      @keyframes dash {
        0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
        50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
        100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
      }
    `}</style>
  </div>
));

const ButtonSpinner = React.memo(() => (
  <>
    <span style={{
      width: '15px',
      height: '15px',
      border: '2px solid rgba(255,255,255,0.4)',
      borderTopColor: '#ffffff',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'cacheSpin 0.7s linear infinite',
    }} />
    <style>{`@keyframes cacheSpin { to { transform: rotate(360deg); } }`}</style>
  </>
));

const AnalyticsPage = () => {
  const [reportType, setReportType] = useState('questionCount');
  const [selectedDimensions, setSelectedDimensions] = useState(INITIAL_PRESET ? INITIAL_PRESET.dimensions : DEFAULT_DIMENSIONS);
  const [selectedMetrics, setSelectedMetrics] = useState(INITIAL_PRESET ? INITIAL_PRESET.metrics : ['Count']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiFilters, setApiFilters] = useState(INITIAL_PRESET && INITIAL_PRESET.apiFilters ? INITIAL_PRESET.apiFilters : {});
  const [localFilters, setLocalFilters] = useState(INITIAL_PRESET && INITIAL_PRESET.localFilters ? INITIAL_PRESET.localFilters : {});
  const [sortRules, setSortRules] = useState([]);
  const [activePreset, setActivePreset] = useState(INITIAL_PRESET ? INITIAL_PRESET.id : null);
  const [activeCriteriaFilter, setActiveCriteriaFilter] = useState(null);
  // Distinct values per column from the last unfiltered load, so server-side
  // filter dropdowns keep listing every option after a filtered refetch.
  const [columnValues, setColumnValues] = useState({});
  const [columnExcludes, setColumnExcludes] = useState(INITIAL_PRESET && INITIAL_PRESET.excludeFilters ? INITIAL_PRESET.excludeFilters : {});
  const [clearingCache, setClearingCache] = useState(false);

  // Apply a preset (or null for "Custom"): sets its dimensions/metrics/filters
  // and marks it active. Used on first load, tab switches, and dropdown picks.
  const applyPreset = useCallback((preset) => {
    setActiveCriteriaFilter(null);
    if (!preset) {
      setActivePreset(null);
      return;
    }
    setActivePreset(preset.id);
    setSelectedDimensions(preset.dimensions);
    setSelectedMetrics(preset.metrics);
    setApiFilters(preset.apiFilters || {});
    setLocalFilters(preset.localFilters || {});
    setColumnExcludes(preset.excludeFilters || {});
    setSortRules([]);
  }, []);

  const handleReportTypeChange = useCallback((newType) => {
    if (newType === reportType) return;
    setRows([]); // Clear existing data immediately
    setLoading(true); // Trigger loader instantly
    setReportType(newType);

    // Open the first grouping of the new tab by default.
    const firstPreset = getPresetsForReport(newType)[0];
    if (firstPreset) {
      applyPreset(firstPreset);
    } else {
      applyPreset(null);
      setSelectedDimensions([]);
      setApiFilters({});
      setLocalFilters({});
      setColumnExcludes({});
      setSortRules([]);
      setSelectedMetrics(newType === 'topicStats'
        ? ['Count']
        : ['TotalCount', 'VideoCount', 'ImageCount', 'CMSReportCount', 'BscReportCount']);
    }
  }, [reportType, applyPreset]);

  const handleClearFilters = useCallback(() => {
    // Keep the active preset's base filters (e.g. RowStatus=true) so clearing
    // doesn't break a group; only user-applied filters are removed.
    const preset = getPresetById(activePreset);
    setApiFilters({});
    setLocalFilters(preset && preset.localFilters ? preset.localFilters : {});
    setColumnExcludes(preset && preset.excludeFilters ? preset.excludeFilters : {});
  }, [activePreset]);

  const handleClearSelection = useCallback(() => {
    setSelectedDimensions([]);
    setSelectedMetrics([]);
    setActivePreset(null);
    setActiveCriteriaFilter(null);
  }, []);

  const handleSelectPreset = useCallback((presetId) => {
    applyPreset(presetId ? getPresetById(presetId) : null);
  }, [applyPreset]);

  const toggleCriteriaFilter = useCallback((key) => {
    setActiveCriteriaFilter((prev) => (prev === key ? null : key));
  }, []);

  const handleRunReport = useCallback(async () => {
    setLoading(true);
    
    try {
      let response;
      if (reportType === 'topicStats') {
        const payload = {
          extraGroupBy: selectedDimensions,
          metrics: selectedMetrics,
          filter: mapFiltersToBackend(apiFilters)
        };
        response = await fetchTopicStatsReport(payload, user.Token);
      } else if (reportType === 'questionCount') {
        const payload = {
          extraGroupBy: selectedDimensions,
          metrics: selectedMetrics, // Metrics are now selectable for this report type too
          filter: mapFiltersToBackend(apiFilters)
        };
        response = await fetchQuestionCountReport(payload, user.Token);
      }

      console.log("response", response);

      if (response && response.data && response.data.ResponseData && response.data.ResponseData.Rows) {
        const transformedData = transformData(response.data.ResponseData.Rows);
        setRows(transformedData);
        // On an unfiltered load we have every value, so refresh the dropdown
        // option cache. Filtered loads are subsets, so keep the fuller list.
        if (Object.keys(apiFilters).length === 0) {
          setColumnValues(collectColumnValues(transformedData));
        }
      } else {
        setRows([]);
        console.error("Invalid response format", response);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [reportType, selectedDimensions, selectedMetrics, apiFilters]);

  const handleClearCache = useCallback(async () => {
    setClearingCache(true);
    try {
      const res = await clearAnalyticsCache(user.Token);
      console.log('[Cache Clear] response:', res);
    } catch (e) {
      console.error('[Cache Clear] failed:', e);
    } finally {
      setClearingCache(false);
      handleRunReport(); // refetch fresh data once the cache is cleared
    }
  }, [handleRunReport]);

  useEffect(() => {
    handleRunReport();
  }, [handleRunReport]);

  // Fall back to "Custom" if the user manually edits a preset's selection.
  useEffect(() => {
    if (!activePreset) return;
    const preset = getPresetById(activePreset);
    if (!preset) return;
    const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
    if (!sameSet(selectedDimensions, preset.dimensions) || !sameSet(selectedMetrics, preset.metrics)) {
      setActivePreset(null);
      setActiveCriteriaFilter(null);
      setColumnExcludes({});
    }
  }, [selectedDimensions, selectedMetrics, activePreset]);

  // Drop local filters whose column no longer exists in the loaded data (e.g.
  // after the dimension they filtered on was removed) so they can't hide rows
  // or linger in the Active Filters count. Skipped while data is empty/loading.
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    setLocalFilters((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const present = collectPresentKeys(rows, keys);
      if (keys.every((k) => present.has(k))) return prev;
      const next = {};
      keys.forEach((k) => { if (present.has(k)) next[k] = prev[k]; });
      return next;
    });
  }, [rows]);

  // Extract dimensions from the first row of data if available, to handle default dimensions
  let actualDimensions = selectedDimensions;
  if (selectedDimensions.length === 0 && rows.length > 0) {
    actualDimensions = Object.keys(rows[0].dimensions);
  } else if (rows.length > 0) {
     // If we have data, we'll ensure we show the dimensions that exist
     const availableDimsFromData = Object.keys(rows[0].dimensions);
     
     // Only use dimensions from data if we haven't selected anything or the selected dimensions don't match the data
     if (availableDimsFromData.length > 0) {
        // Collect all unique dimension keys from the first row and its nested children
        const extractAllDims = (row, currentDims = new Set()) => {
            Object.keys(row.dimensions || {}).forEach(k => currentDims.add(k));
            if (row.children && row.children.length > 0) {
                extractAllDims(row.children[0], currentDims);
            }
            return Array.from(currentDims);
        };
        actualDimensions = extractAllDims(rows[0]);
     }
  }

  // To make sure columns are ordered nicely, we can use actualDimensions
  let dimensionsCols = actualDimensions;
  
  let metricsCols = selectedMetrics;

  const columns = [...dimensionsCols, ...metricsCols];

  // const filteredData = useMemo(() => filterNodes(rows, localFilters), [rows, localFilters]);
  // const processedData = useMemo(() => sortNodes(filteredData, sortRules), [filteredData, sortRules]);

  // const leafRows = useMemo(() => collectLeafNodes(processedData), [processedData]);
  const availablePresets = useMemo(() => getPresetsForReport(reportType), [reportType]);
  const presetObj = useMemo(() => getPresetById(activePreset), [activePreset]);
  const excludeColumns = useMemo(() => Object.keys((presetObj && presetObj.excludeFilters) || {}), [presetObj]);

  const metricKeySet = useMemo(() => new Set(selectedMetrics), [selectedMetrics]);
  const filteredData = useMemo(() => excludeNodes(filterNodes(rows, localFilters, metricKeySet), columnExcludes), [rows, localFilters, columnExcludes, metricKeySet]);
  const processedData = useMemo(() => sortNodes(filteredData, sortRules), [filteredData, sortRules]);
  const leafRows = useMemo(() => collectLeafNodes(processedData), [processedData]);


  // Each criterion declares a predicate; the same predicate drives both the
  // displayed count and the click-to-filter drill-in, so they always agree.
  const criteriaItems = useMemo(() => {
    if (!presetObj || !Array.isArray(presetObj.criteria)) return null;
    return presetObj.criteria.map((item) => ({
      section: item.section,
      label: item.label,
      segments: item.segments.map((seg) => ({
        key: seg.key,
        tone: seg.tone,
        animate: seg.animate,
        value: leafRows.reduce((acc, leaf) => acc + (seg.match(leaf) ? 1 : 0), 0),
      })),
    }));
  }, [presetObj, leafRows]);
  const criteriaTones = useMemo(() => {
    if (!criteriaItems) return [];
    const present = new Set();
    criteriaItems.forEach((item) => item.segments.forEach((seg) => present.add(seg.tone)));
    return ['success', 'warning', 'danger', 'critical'].filter((tone) => present.has(tone));
  }, [criteriaItems]);

  // Drill-in: when a criterion number is clicked, the table shows only its rows
  // and tints them with that criterion's tone color.
  const activeCriterion = useMemo(() => {
    if (!activeCriteriaFilter || !presetObj || !Array.isArray(presetObj.criteria)) return null;
    for (const item of presetObj.criteria) {
      const seg = item.segments.find((s) => s.key === activeCriteriaFilter);
      if (seg) return seg;
    }
    return null;
  }, [activeCriteriaFilter, presetObj]);
  const activeMatch = activeCriterion ? activeCriterion.match : null;
  const activeColor = activeCriterion ? TONE_COLORS[activeCriterion.tone] : null;
  const tableData = useMemo(
    () => (activeMatch ? filterTreeByLeafPredicate(processedData, activeMatch) : processedData),
    [processedData, activeMatch]
  );

  // Flat view: show only leaf rows (each already carries its full dimension
  // path); parent/aggregate rows are dropped and there are no expand toggles.
  // Sorting is applied to the flat list so a column sort orders the whole table
  // globally, not just within each group.
  const displayRows = useMemo(
    () => sortFlatRows(collectLeafNodes(tableData).map((row) => ({ ...row, level: 0 })), sortRules),
    [tableData, sortRules]
  );

  const summaryStats = useMemo(() => {
    // The preset's base filters (e.g. RowStatus) aren't "user" filters, so they
    // don't count toward Active Filters or enable the Clear Filters button.
    const baseKeys = presetObj && presetObj.localFilters ? Object.keys(presetObj.localFilters) : [];
    const filterCount = Object.keys(apiFilters).length
      + Object.keys(localFilters).filter((k) => !baseKeys.includes(k)).length;
    return { filterCount, totalCount: displayRows.length };
  }, [displayRows, apiFilters, localFilters, presetObj]);

  return (
    <>
    <SideBar />
    <div style={{marginLeft: Components.SideBarWidth + 15, padding: '20px', fontFamily: 'Arial, sans-serif' }}>
    <BackButton />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <h1 style={{ margin: 0 }}>Generic Analytics Reporting</h1>
        <button
           onClick={handleClearCache}
           disabled={clearingCache}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: clearingCache ? 'not-allowed' : 'pointer',
            opacity: clearingCache ? 0.7 : 1,
            fontWeight: 'bold',
            minWidth: '120px',
            height: '37px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
         {clearingCache ? <ButtonSpinner /> : 'Cache Clear'}
        </button>
      </div>
      
      <div style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <ReportSelector
            reportType={reportType}
            setReportType={handleReportTypeChange}
          />

          {availablePresets.length > 0 && (
            <GroupingSelector
              presets={availablePresets}
              activePreset={activePreset}
              onSelect={handleSelectPreset}
            />
          )}

          <DimensionSelector
            reportType={reportType}
            selectedDimensions={selectedDimensions}
            setSelectedDimensions={setSelectedDimensions}
          />

          <MetricSelector
            reportType={reportType}
            selectedMetrics={selectedMetrics}
            setSelectedMetrics={setSelectedMetrics}
            selectedDimensions={selectedDimensions}
          />

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleRunReport}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>

            <ExportCSV data={displayRows} columns={columns} />

            <button
              onClick={handleClearFilters}
              disabled={summaryStats.filterCount === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: summaryStats.filterCount > 0 ? '#dc3545' : '#e9ecef',
                color: summaryStats.filterCount > 0 ? 'white' : '#adb5bd',
                border: 'none',
                borderRadius: '4px',
                cursor: summaryStats.filterCount > 0 ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              Clear Filters
            </button>

            <button
              onClick={handleClearSelection}
              disabled={selectedDimensions.length === 0 && selectedMetrics.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: (selectedDimensions.length > 0 || selectedMetrics.length > 0) ? '#6c757d' : '#e9ecef',
                color: (selectedDimensions.length > 0 || selectedMetrics.length > 0) ? 'white' : '#adb5bd',
                border: 'none',
                borderRadius: '4px',
                cursor: (selectedDimensions.length > 0 || selectedMetrics.length > 0) ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              Clear Checkboxes
            </button>
          </div>
        </div>

        <div style={{
          minWidth: '220px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#555', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
            Summary
          </div>
          {[
            { label: 'Active Filters', value: summaryStats.filterCount, color: summaryStats.filterCount > 0 ? '#dc3545' : '#333' },
            { label: 'Total Count', value: summaryStats.totalCount, color: '#333' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
              <span style={{ color: '#666' }}>{label}</span>
              <span style={{ fontWeight: 'bold', color, fontSize: '15px' }}>{value}</span>
            </div>
          ))}

          {criteriaItems && (
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <style>{`
                @keyframes criteriaPulse {
                  0%, 100% { background-color: rgba(220,53,69,0); }
                  50% { background-color: rgba(220,53,69,0.5); }
                }
              `}</style>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>
                {presetObj.criteriaTitle || 'Criteria'}
              </div>
              {criteriaTones.length > 0 && (
                <div style={{ fontSize: '11px', color: '#999', display: 'flex', gap: '12px', marginTop: '-4px', flexWrap: 'wrap' }}>
                  {criteriaTones.map((tone) => (
                    <span key={tone}>
                      <span style={{ color: TONE_COLORS[tone], fontWeight: 'bold' }}>●</span> {TONE_LABELS[tone]}
                    </span>
                  ))}
                </div>
              )}
              {criteriaItems.map((item, idx) => {
                const prevSection = idx > 0 ? criteriaItems[idx - 1].section : null;
                const showSection = item.section && item.section !== prevSection;
                const rowKey = item.segments[0] ? item.segments[0].key : item.label;
                return (
                  <React.Fragment key={rowKey}>
                    {showSection && (
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#777', marginTop: idx > 0 ? '4px' : 0 }}>
                        {item.section}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', gap: '10px' }}>
                      <span style={{ color: '#666' }}>{item.label}</span>
                      <span style={{ display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                        {item.segments.map((seg, sidx) => {
                          const clickable = seg.value > 0;
                          const isActive = activeCriteriaFilter === seg.key;
                          return (
                            <React.Fragment key={seg.key}>
                              {sidx > 0 && <span style={{ color: '#ccc' }}>/</span>}
                              <span
                                onClick={clickable ? () => toggleCriteriaFilter(seg.key) : undefined}
                                title={clickable ? (isActive ? 'Showing only these rows — click to show all' : 'Show only these rows') : undefined}
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '15px',
                                  color: seg.value > 0 ? TONE_COLORS[seg.tone] : MUTED_COLOR,
                                  cursor: clickable ? 'pointer' : 'default',
                                  padding: '0 6px',
                                  borderRadius: '4px',
                                  textDecoration: isActive ? 'underline' : 'none',
                                  backgroundColor: isActive ? `${TONE_COLORS[seg.tone]}22` : 'transparent',
                                  animation: (seg.animate && seg.value > 0) ? 'criteriaPulse 1.1s ease-in-out infinite' : undefined,
                                }}
                              >
                                {seg.value}
                              </span>
                            </React.Fragment>
                          );
                        })}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
              {activeCriteriaFilter && (
                <div style={{ fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Table filtered to selection</span>
                  <span onClick={() => setActiveCriteriaFilter(null)} style={{ color: '#007bff', cursor: 'pointer', fontWeight: 'bold' }}>
                    show all
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div style={{ position: 'relative', minHeight: '200px' }}>
        <div style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s', pointerEvents: loading ? 'none' : 'auto' }}>
          <AnalyticsTable
            data={displayRows}
            rawData={rows}
            columns={columns} 
            dimensions={dimensionsCols} 
            metrics={metricsCols} 
            apiFilters={apiFilters}
            setApiFilters={setApiFilters}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            columnValues={columnValues}
            columnExcludes={columnExcludes}
            excludeColumns={excludeColumns}
            setColumnExcludes={setColumnExcludes}
            sortRules={sortRules}
            setSortRules={setSortRules}
            highlightMatch={activeMatch}
            highlightColor={activeColor}
          />
        </div>

        {loading && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            backgroundColor: 'rgba(255,255,255,0.4)',
            zIndex: 100,
            borderRadius: '4px'
          }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#28a745', fontSize: '16px' }}>
              {rows.length > 0 ? 'Updating data...' : 'Loading data...'}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default AnalyticsPage;
